import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, resolveMediaUrl } from '../utils/api';
import UserIcon from './icons/UserIcon';
import SalonsIcon from './icons/SalonsIcon';
import BookingsIcon from './icons/BookingsIcon';
import ProductsIcon from './icons/ProductsIcon';
import ServicesIcon from './icons/ServicesIcon';
import SeatsIcon from './icons/SeatsIcon';
import OrdersIcon from './icons/OrdersIcon';
import TimeSlotsIcon from './icons/TimeSlotsIcon';

/* ── Design System ───────────────────────────────────────────────────── */
// Sidebar is always dark navy (#0f172a). Accent color changes per role.
const SIDEBAR_BG    = '#0f172a';
const SIDEBAR_HOVER = '#1e293b';
const SIDEBAR_TEXT  = '#94a3b8';   // slate-400
const SIDEBAR_ACTIVE_TEXT = '#f8fafc';  // almost white

const T_ORANGE = {
    bg: '#f1f5f9',
    bgCard: '#ffffff',
    bgCard2: '#f8fafc',
    border: '#e2e8f0',
    orange: '#0ea5e9',          // sky-500 — Admin accent
    orangeHov: '#0284c7',
    text: '#334155',
    muted: '#64748b',
    white: '#0f172a',           // darkest text
    danger: '#fee2e2',
    dangerHov: '#ef4444',
    green: '#dcfce7',
    greenText: '#16a34a',
    yellow: '#fef3c7',
    yellowText: '#d97706',
    red: '#fee2e2',
    redText: '#dc2626',
};

const T_PURPLE = { ...T_ORANGE, orange: '#a855f7', orangeHov: '#9333ea' };  // Manager (women/nails)
const T_BRANCH = { ...T_ORANGE, orange: '#10b981', orangeHov: '#059669' };  // Branch Manager
const T_WORKER = { ...T_ORANGE, orange: '#f97316', orangeHov: '#ea580c' };  // Worker

export const T = T_ORANGE;

function buildS(T) {
    return {
        page: {
            minHeight: '100vh',
            background: T.bg,
            fontFamily: "'DM Sans', sans-serif",
            color: T.text,
            display: 'flex',
        },
        sidebar: {
            width: '240px',
            flexShrink: 0,
            background: SIDEBAR_BG,
            display: 'flex',
            flexDirection: 'column',
            padding: '0',
            minHeight: '100vh',
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto',
        },
        main: {
            flex: 1,
            padding: '2rem 2.5rem',
            overflowX: 'auto',
            minWidth: 0,
        },
        pageTitle: {
            fontFamily: "'Sora', sans-serif",
            fontSize: '1.6rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: T.white,
            marginBottom: '0.25rem',
        },
        card: {
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        },
        statCard: {
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        statVal: {
            fontFamily: "'Sora', sans-serif",
            fontSize: '2rem',
            fontWeight: 700,
            color: T.white,
            lineHeight: 1.2,
        },
        statLabel: {
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.8rem',
            fontWeight: 500,
            color: T.muted,
            marginTop: '0.3rem',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
        },
        th: {
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: T.muted,
            padding: '1rem',
            textAlign: 'left',
            borderBottom: `2px solid ${T.border}`,
            background: T.bgCard2,
        },
        td: {
            padding: '1rem',
            borderBottom: `1px solid ${T.border}`,
            color: T.text,
            verticalAlign: 'middle',
            fontFamily: "'DM Sans', sans-serif",
        },
        input: {
            width: '100%',
            padding: '0.6rem 0.85rem',
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: '8px',
            color: T.text,
            fontSize: '0.9rem',
            outline: 'none',
            marginBottom: '0.85rem',
            fontFamily: "'DM Sans', sans-serif",
            transition: 'border-color 0.2s',
        },
        select: {
            width: '100%',
            padding: '0.6rem 0.85rem',
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: '8px',
            color: T.text,
            fontSize: '0.9rem',
            outline: 'none',
            marginBottom: '0.85rem',
            fontFamily: "'DM Sans', sans-serif",
            transition: 'border-color 0.2s',
        },
        btnOrange: {
            background: T.orange,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '0.5rem 1.25rem',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s',
        },
        btnOutline: {
            background: 'transparent',
            color: T.orange,
            border: `1.5px solid ${T.orange}`,
            borderRadius: '8px',
            padding: '0.5rem 1.25rem',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        btnDanger: {
            background: 'transparent',
            color: T.dangerHov,
            border: '1px solid rgba(239,68,68,0.35)',
            borderRadius: '8px',
            padding: '0.35rem 0.85rem',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: 'pointer',
        },
        btnSmall: {
            background: 'transparent',
            color: T.muted,
            border: `1px solid ${T.border}`,
            borderRadius: '6px',
            padding: '0.35rem 0.85rem',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.8rem',
            fontWeight: 500,
            cursor: 'pointer',
        },
        modal: {
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '1rem',
        },
        modalBox: {
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
        modalTitle: {
            fontFamily: "'Sora', sans-serif",
            fontSize: '1.2rem',
            fontWeight: 700,
            color: T.white,
            marginBottom: '1.25rem',
        },
        badge: (statusVal) => {
            const base = {
                padding: '0.2rem 0.65rem',
                borderRadius: '999px',
                fontSize: '0.7rem',
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                letterSpacing: '0.02em',
                display: 'inline-block',
            };
            if (statusVal === 'confirmed') return { ...base, background: '#dcfce7', color: '#15803d' };
            if (statusVal === 'cancelled') return { ...base, background: '#fee2e2', color: '#b91c1c' };
            if (statusVal === 'no_show')   return { ...base, background: '#431407', color: '#fdba74' };
            if (statusVal === 'completed') return { ...base, background: '#e0f2fe', color: '#0369a1' };
            return { ...base, background: T.yellow, color: T.yellowText };
        },
    };
}

export const S = buildS(T_ORANGE);

const ThemeContext = createContext({ T: T_ORANGE, S: buildS(T_ORANGE) });

export function useTheme() {
    return useContext(ThemeContext);
}

/* ── Sidebar nav link ─────────────────────────────────────────────── */
function NavItem({ to, label, icon, onNavigate }) {
    const { pathname } = useLocation();
    const { i18n } = useTranslation();
    const { T } = useTheme();
    const active = pathname === to || (to !== '/admin' && to !== '/manager' && to !== '/worker' && pathname.startsWith(to));
    const isRTL = i18n.language === 'ar';

    return (
        <Link
            to={to}
            onClick={onNavigate}
            className={active ? 'admin-nav-link active' : 'admin-nav-link'}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.6rem 1.2rem',
                margin: '0.1rem 0.75rem',
                color: active ? SIDEBAR_ACTIVE_TEXT : SIDEBAR_TEXT,
                textDecoration: 'none',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.875rem',
                fontWeight: active ? 600 : 400,
                background: active ? `${T.orange}20` : 'transparent',
                borderRadius: '8px',
                borderLeft: active && !isRTL ? `3px solid ${T.orange}` : '3px solid transparent',
                borderRight: active && isRTL ? `3px solid ${T.orange}` : '3px solid transparent',
                transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
                if (!active) {
                    e.currentTarget.style.color = SIDEBAR_ACTIVE_TEXT;
                    e.currentTarget.style.background = SIDEBAR_HOVER;
                }
            }}
            onMouseLeave={e => {
                if (!active) {
                    e.currentTarget.style.color = SIDEBAR_TEXT;
                    e.currentTarget.style.background = 'transparent';
                }
            }}
        >
            {icon && (
                <span style={{
                    fontSize: '1.1rem',
                    color: active ? T.orange : SIDEBAR_TEXT,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                }}>
                    {icon}
                </span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
            </span>
        </Link>
    );
}

function getAdminLinks(t) {
    return [
        { to: '/admin', label: t('admin.dashboard'), icon: '▦' },
        { to: '/admin/users', label: t('admin.users'), icon: <UserIcon size={18} /> },
        { to: '/admin/shops', label: t('admin.salons'), icon: <SalonsIcon size={18} /> },
        { to: '/admin/bookings', label: t('admin.bookings'), icon: <BookingsIcon size={18} /> },
        { to: '/admin/artists', label: t('admin.workers'), icon: <UserIcon size={18} /> },
        { to: '/admin/attendance', label: t('attendance.title'), icon: '✓' },
        { to: '/admin/handoff-protocol', label: t('premium.handoff', 'Handoff Protocol'), icon: '🔄' },
        { to: '/admin/products', label: t('admin.products'), icon: <ProductsIcon size={18} /> },
        { to: '/admin/services', label: t('admin.services'), icon: <ServicesIcon size={18} /> },
        { to: '/admin/bundles', label: t('admin.bundles.title'), icon: <ServicesIcon size={18} /> },
        { to: '/admin/seats', label: t('admin.seats'), icon: <SeatsIcon size={18} /> },
        { to: '/admin/orders', label: t('admin.orders'), icon: <OrdersIcon size={18} /> },
        { to: '/admin/time-slots', label: t('admin.timeSlots'), icon: <TimeSlotsIcon size={18} /> },
        { to: '/admin/financial-report', label: t('admin.financialReport'), icon: '💰' },
        { to: '/admin/premium-requests', label: t('premium.requests', 'VIP Requests'), icon: '👑' },
        { to: '/admin/premium-users', label: t('premium.users', 'VIP Users'), icon: '💎' },
        { to: '/admin/premium-settings', label: t('premium.settings', 'VIP Settings'), icon: '⚙️' },
        { to: '/admin/vip-calendar', label: t('premium.calendar', 'VIP Calendar'), icon: '📅' },
    ];
}

function getManagerLinks(t) {
    return [
        { to: '/manager', label: t('admin.dashboard'), icon: '▦' },
        { to: '/manager/reservations', label: t('admin.reservations'), icon: '📝' },
        { to: '/manager/bookings', label: t('admin.bookings'), icon: <BookingsIcon size={18} /> },
        { to: '/manager/artists', label: t('admin.workers'), icon: <UserIcon size={18} /> },
        { to: '/manager/branches', label: t('admin.branches'), icon: '🏪' },
        { to: '/manager/attendance', label: t('attendance.title'), icon: '✓' },
        { to: '/manager/products', label: t('admin.products'), icon: <ProductsIcon size={18} /> },
        { to: '/manager/services', label: t('admin.services'), icon: <ServicesIcon size={18} /> },
        { to: '/manager/bundles', label: t('admin.bundles.title'), icon: <ServicesIcon size={18} /> },
        { to: '/manager/seats', label: t('admin.seats'), icon: <SeatsIcon size={18} /> },
        { to: '/manager/analytics', label: t('admin.analytics'), icon: '📊' },
        { to: '/manager/financial-report', label: t('admin.financialReport'), icon: '💰' },
        { to: '/manager/vip-requests', label: t('premium.requests', 'VIP Requests'), icon: '👑' },
        { to: '/manager/vip-members', label: t('premium.members', 'VIP Members'), icon: '💎' },
        { to: '/manager/vip-calendar', label: t('premium.calendar', 'VIP Calendar'), icon: '📅' },
        { to: '/manager/handoff-protocol', label: t('premium.handoff', 'Handoff Protocol'), icon: '🔄' },
    ];
}

function getWorkerLinks(t) {
    return [
        { to: '/worker', label: "Today's Work", icon: '📋' },
        { to: '/worker/schedule', label: 'Schedule', icon: '🗓' },
    ];
}

function RoleBadge({ role }) {
    const colors = {
        admin:   { bg: '#0ea5e9', label: 'ADMIN' },
        manager: { bg: '#a855f7', label: 'MANAGER' },
        branch:  { bg: '#10b981', label: 'BRANCH MGR' },
        worker:  { bg: '#f97316', label: 'WORKER' },
    };
    const c = colors[role] || colors.admin;
    return (
        <span style={{
            fontSize: '0.6rem',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: c.bg,
            background: `${c.bg}18`,
            border: `1px solid ${c.bg}40`,
            borderRadius: '4px',
            padding: '0.15rem 0.5rem',
            display: 'inline-block',
            marginTop: '0.25rem',
        }}>
            {c.label}
        </span>
    );
}

function LanguageSwitcher({ accent }) {
    const { i18n } = useTranslation();
    const isAr = i18n.language === 'ar';
    const btnBase = {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.7rem',
        fontWeight: 600,
        padding: '0.25rem 0.55rem',
        border: `1px solid ${accent}40`,
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.15s',
    };
    return (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button type="button" onClick={() => i18n.changeLanguage('en')}
                style={{ ...btnBase, background: !isAr ? accent : 'transparent', color: !isAr ? '#fff' : SIDEBAR_TEXT }}>
                EN
            </button>
            <button type="button" onClick={() => i18n.changeLanguage('ar')}
                style={{ ...btnBase, background: isAr ? accent : 'transparent', color: isAr ? '#fff' : SIDEBAR_TEXT }}>
                AR
            </button>
        </div>
    );
}

function getFirstName(name) {
    if (!name || typeof name !== 'string') return '';
    const trimmedName = name.trim();
    if (!trimmedName) return '';
    return trimmedName.split(/\s+/)[0] || '';
}

/* ── Main exported layout ─────────────────────────────────────────── */
const MOBILE_BREAKPOINT = 768;

export default function AdminLayout({ children, role = 'admin' }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
        const onMatch = (e) => { setIsMobile(e.matches); if (!e.matches) setSidebarOpen(false); };
        onMatch(mql);
        mql.addEventListener('change', onMatch);
        return () => mql.removeEventListener('change', onMatch);
    }, []);

    const links = role === 'admin'
        ? getAdminLinks(t)
        : role === 'worker'
            ? getWorkerLinks(t)
            : getManagerLinks(t);

    const isFemaleTheme = role === 'manager' && user && ['women', 'nails'].includes(user.shop_category);
    const isBranchTheme = role === 'manager' && user && user.role === 'branch_manager';
    const themeT = isFemaleTheme ? T_PURPLE
        : isBranchTheme ? T_BRANCH
        : role === 'worker' ? T_WORKER
        : T_ORANGE;
    const themeS = useMemo(() => buildS(themeT), [themeT]);
    const isRTL = i18n.language === 'ar';
    const firstName = getFirstName(user?.name);

    const sidebarStyle = useMemo(() => ({
        ...themeS.sidebar,
        ...(isRTL ? { borderRight: 'none', borderLeft: '1px solid #1e293b' } : {}),
    }), [themeS.sidebar, isRTL]);
    const thStyle = useMemo(() => ({
        ...themeS.th,
        ...(isRTL ? { textAlign: 'right' } : {}),
    }), [themeS.th, isRTL]);
    const themeValue = useMemo(() => ({
        T: themeT,
        S: { ...themeS, sidebar: sidebarStyle, th: thStyle },
    }), [themeT, themeS, sidebarStyle, thStyle]);

    const handleLogout = () => { logout(); navigate('/login'); };
    const closeSidebar = () => setSidebarOpen(false);

    const displayRole = role === 'worker' ? 'worker'
        : isBranchTheme ? 'branch'
        : role === 'manager' ? 'manager'
        : 'admin';

    return (
        <ThemeContext.Provider value={themeValue}>
            <div className={`admin-layout${sidebarOpen ? ' admin-layout-sidebar-open' : ''}`} style={themeS.page}>
                {/* Mobile top bar */}
                <div className="admin-layout-mobile-bar" aria-hidden={!isMobile} style={{ display: 'none' }}>
                    <button type="button" onClick={() => setSidebarOpen(true)} aria-label={t('common.menu') || 'Menu'}
                        style={{ background: 'none', border: 'none', color: themeT.orange, cursor: 'pointer', padding: '0.5rem', fontSize: '1.5rem' }}>
                        ☰
                    </button>
                    <Link to="/" onClick={closeSidebar} style={{ display: 'flex', alignItems: 'center' }}>
                        <img src="/Logo-Yalla-Salon.png" alt="Yalla Salon" style={{ height: '36px', width: 'auto' }} />
                    </Link>
                    <span style={{ width: 40 }} />
                </div>

                {/* Sidebar overlay (mobile) */}
                {isMobile && <div className="admin-layout-overlay" onClick={closeSidebar} aria-hidden="true" />}

                {/* Sidebar */}
                <aside className="admin-layout-sidebar" style={sidebarStyle}>
                    {/* Logo + User header */}
                    <div style={{
                        padding: '1.5rem 1.25rem 1.25rem',
                        borderBottom: '1px solid #1e293b',
                    }}>
                        {/* Logo */}
                        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem', textDecoration: 'none' }}>
                            <img src="/Logo-Yalla-Salon.png" alt="Yalla Salon" style={{ height: '32px', width: 'auto' }} />
                            <span style={{ fontFamily: "'Sora', sans-serif", fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.01em' }}>
                                Yalla Salon
                            </span>
                        </Link>

                        {/* User info */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {user?.profile_picture_url ? (
                                <img src={resolveMediaUrl(user.profile_picture_url)} alt=""
                                    style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${themeT.orange}40` }} />
                            ) : (
                                <div style={{
                                    width: 38, height: 38, borderRadius: '50%',
                                    background: `${themeT.orange}20`,
                                    border: `2px solid ${themeT.orange}50`,
                                    color: themeT.orange,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.9rem', fontWeight: 700,
                                    flexShrink: 0,
                                }}>
                                    {firstName?.[0]?.toUpperCase() || '?'}
                                </div>
                            )}
                            <div style={{ minWidth: 0 }}>
                                <div style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.85rem', fontWeight: 600,
                                    color: SIDEBAR_ACTIVE_TEXT,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {firstName || user?.name || 'User'}
                                </div>
                                <RoleBadge role={displayRole} />
                            </div>
                        </div>
                    </div>

                    {/* Nav links */}
                    <nav style={{ flex: 1, padding: '0.75rem 0', overflowY: 'auto' }}>
                        {links.map(l => <NavItem key={l.to} {...l} onNavigate={closeSidebar} />)}
                    </nav>

                    {/* Footer: language + logout */}
                    <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #1e293b' }}>
                        <div style={{ marginBottom: '0.75rem' }}>
                            <LanguageSwitcher accent={themeT.orange} />
                        </div>
                        <button
                            onClick={handleLogout}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                width: '100%',
                                background: 'none',
                                border: 'none',
                                color: '#f87171',
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                padding: '0.4rem 0',
                                transition: 'color 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={e => e.currentTarget.style.color = '#f87171'}
                        >
                            <span style={{ fontSize: '1rem' }}>⎋</span>
                            {t('nav.logout')}
                        </button>
                    </div>
                </aside>

                {/* Page content */}
                <main className="admin-layout-main" style={themeS.main}>
                    {children}
                </main>
            </div>
        </ThemeContext.Provider>
    );
}

/* Named exports for consumers */
export { T_ORANGE, T_PURPLE, T_BRANCH, T_WORKER, buildS };
